import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Map, { Marker } from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  InputAdornment,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import MapIcon from '@mui/icons-material/Map';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import PageHeader from '../../components/common/PageHeader';
import api from '../../api/axiosInstance';

const formatDate = (value) => (value ? new Date(value).toLocaleDateString('fr-FR') : '-');

const hasCoordinates = (site) => Number.isFinite(Number(site?.latitude))
  && Number.isFinite(Number(site?.longitude));

const getInitialViewState = (points) => {
  if (!points.length) return { longitude: 55.5364, latitude: -21.1151, zoom: 8.2 };
  const longitude = points.reduce((sum, point) => sum + Number(point.longitude), 0) / points.length;
  const latitude = points.reduce((sum, point) => sum + Number(point.latitude), 0) / points.length;
  return { longitude, latitude, zoom: points.length === 1 ? 13 : 9 };
};

export default function ClientSites() {
  const mapRef = useRef(null);
  const [sites, setSites] = useState([]);
  const [organization, setOrganization] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedSiteId, setSelectedSiteId] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    api.get('/api/client/sites')
      .then(({ data }) => {
        if (!active) return;
        setSites(Array.isArray(data?.data) ? data.data : []);
        setOrganization(data?.organization || null);
      })
      .catch(() => active && setError('Impossible de charger les sites de votre organisation.'))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, []);

  const filteredSites = useMemo(() => {
    const query = search.trim().toLocaleLowerCase('fr-FR');
    if (!query) return sites;
    return sites.filter((site) => [site.name, site.address, site.geocodedAddress]
      .some((value) => String(value || '').toLocaleLowerCase('fr-FR').includes(query)));
  }, [search, sites]);

  const positionedSites = sites.filter((site) => Number.isFinite(site.latitude) && Number.isFinite(site.longitude)).length;
  const mappedSites = useMemo(() => filteredSites.filter(hasCoordinates), [filteredSites]);

  const fitMapToPoints = useCallback(() => {
    if (!mapRef.current || mappedSites.length < 2) return;
    const bounds = mappedSites.reduce((acc, site) => {
      const longitude = Number(site.longitude);
      const latitude = Number(site.latitude);
      return [
        [Math.min(acc[0][0], longitude), Math.min(acc[0][1], latitude)],
        [Math.max(acc[1][0], longitude), Math.max(acc[1][1], latitude)],
      ];
    }, [[Infinity, Infinity], [-Infinity, -Infinity]]);
    mapRef.current.fitBounds(bounds, { padding: 70, maxZoom: 13, duration: 650 });
  }, [mappedSites]);

  useEffect(() => {
    const timeout = setTimeout(fitMapToPoints, 250);
    return () => clearTimeout(timeout);
  }, [fitMapToPoints]);

  const focusSiteOnMap = useCallback((site) => {
    if (!site || !hasCoordinates(site) || !mapRef.current) return;
    setSelectedSiteId(String(site._id || site.hudu_id || ''));
    mapRef.current.flyTo({
      center: [Number(site.longitude), Number(site.latitude)],
      zoom: 15,
      duration: 800,
      essential: true,
    });
  }, []);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        section={organization?.name || 'Clients'}
        title="Vos sites"
        subtitle="Sites rattachés à votre organisation."
        sx={{ mb: 3 }}
      />

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
        <Box sx={{ flex: 1 }} />
        <TextField
          size="small"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Rechercher un site ou une adresse"
          sx={{ width: { xs: '100%', sm: 320 } }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
          }}
        />
      </Stack>

      <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 2 }}>
        <Stack spacing={1}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Carte des sites</Typography>
            <Chip size="small" icon={<MapIcon />} variant="outlined" label={`${mappedSites.length} point(s) affiché(s)`} />
          </Stack>
          <Box sx={{ height: { xs: 330, md: 430 }, borderRadius: 1.5, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
            {process.env.REACT_APP_MAPBOX_TOKEN ? (
              <Map
                ref={mapRef}
                mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
                initialViewState={getInitialViewState(mappedSites)}
                onLoad={fitMapToPoints}
                style={{ width: '100%', height: '100%' }}
                mapStyle="mapbox://styles/mapbox/streets-v12"
              >
                {mappedSites.map((site) => {
                  const siteId = String(site._id || site.hudu_id || '');
                  const selected = selectedSiteId === siteId;
                  return (
                    <Marker key={siteId || `${site.name}-${site.latitude}-${site.longitude}`} longitude={Number(site.longitude)} latitude={Number(site.latitude)} anchor="bottom">
                      <Box
                        role="button"
                        tabIndex={0}
                        title={`${site.name || 'Site'}${site.address ? ` - ${site.address}` : ''}`}
                        onClick={() => focusSiteOnMap(site)}
                        onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') focusSiteOnMap(site); }}
                        sx={{
                          width: selected ? 38 : 30,
                          height: selected ? 38 : 30,
                          borderRadius: '50% 50% 50% 0',
                          transform: 'rotate(-45deg)',
                          bgcolor: selected ? '#f59e0b' : '#0f7b4d',
                          border: '2px solid #fff',
                          boxShadow: 3,
                          display: 'grid',
                          placeItems: 'center',
                          cursor: 'pointer',
                          transition: 'all 160ms ease',
                        }}
                      >
                        <PlaceRoundedIcon sx={{ color: '#fff', fontSize: 18, transform: 'rotate(45deg)' }} />
                      </Box>
                    </Marker>
                  );
                })}
              </Map>
            ) : (
              <Stack alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
                <Typography color="text.secondary">Token Mapbox non configuré.</Typography>
              </Stack>
            )}
          </Box>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 680 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 800 }}>Site</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Adresse</TableCell>
                <TableCell sx={{ fontWeight: 800, width: 72 }}>Carte</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={3} align="center" sx={{ py: 6 }}><CircularProgress size={24} /></TableCell></TableRow>
              ) : filteredSites.map((site) => (
                <TableRow key={site._id || `${site.name}-${site.address}`} hover>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{site.name || 'Site sans nom'}</TableCell>
                  <TableCell sx={{ minWidth: 280 }}>{site.address || '-'}</TableCell>
                  <TableCell>
                    <IconButton size="small" color="success" disabled={!hasCoordinates(site)} onClick={() => focusSiteOnMap(site)} title={hasCoordinates(site) ? 'Centrer la carte sur ce site' : 'Position indisponible'}>
                      <PlaceRoundedIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && !filteredSites.length ? (
                <TableRow><TableCell colSpan={3} align="center" sx={{ py: 6, color: 'text.secondary' }}>Aucun site trouvé.</TableCell></TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {!loading && filteredSites.length ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {filteredSites.length} site(s) affiché(s)
        </Typography>
      ) : null}
    </Box>
  );
}
